import createClient, { type Middleware } from 'openapi-fetch';

import { getToken } from '@/lib/auth/token-store';
import { isNativeApp } from '@/lib/platform';
import { safeLocalStorage } from '@/lib/safe-storage';

import { API_CREDENTIALS, API_ORIGIN } from './origin';
import type { paths } from './schema';

/**
 * =============================================================================
 *  API client —— ARCHITECTURE §2.6「API 型別」那一列的實作。
 *
 *  `openapi-fetch` + `openapi-typescript` 產生的 `paths`，所以：
 *    · 路徑打錯 → 編譯失敗
 *    · 少帶一個必填 query param → 編譯失敗
 *    · 回應欄位名改了 → 讀那個欄位的地方編譯失敗
 *
 *  手寫 `fetch` 與手刻 interface 是被明確否決的方案，ESLint 的
 *  `no-restricted-globals: fetch` 則讓「偷偷寫一個 fetch」這條路在建置期就斷掉。
 *
 *  baseUrl 是 `lib/api/origin.ts` 的 `API_ORIGIN`：網站建置時是空字串（相對路徑，dev 走
 *  vite proxy、正式走 Caddy 同源）；只有 iOS App 的建置設 `VITE_API_ORIGIN`（跨來源、
 *  Bearer、不帶 cookie）。網站上**沒有任何 base URL 常數**，也就沒有「忘了改環境變數」
 *  這個失效模式。
 * ========================================================================== */

/**
 * dev fixture 偵測（tools/fixture-api.mjs）。
 *
 * fixture 的每個回應都帶 `X-Stock-Fixture: 1`。前端讀到就在 TopNav 亮一個
 * 琥珀色 chip。**畫面永遠不會假裝自己在看真資料**——一個看起來像正式環境
 * 的假資料截圖，比沒有截圖更糟。
 *
 * **P2 追加：這個旗標必須被記住。** `persistQueryClient`（§10.2 規則 1）上線之後，
 * 第二次載入可能整頁都從 IndexedDB 畫出來、一個請求都不發——於是沒有任何回應
 * 帶得回那個標頭，chip 就熄了，而畫面上的數字仍然是 fixture 的。
 * 那正好是這個 chip 存在要防的情況，所以旗標存進 localStorage：
 * 開機先假設上次的狀態，第一個**不帶**標頭的回應會把它關掉。
 */
const FIXTURE_FLAG_KEY = 'stock.fixtureData';

/**
 * dev server 是不是用 `npm run dev:fixtures` 起的。這是**最強的訊號**：
 * 它在第一幀就知道，不需要等任何回應，也不會被快取命中蓋過去。
 * 正式 build 這個值恆為 undefined，整段程式碼因此被 tree-shake 掉。
 */
const FIXTURE_BUILD = import.meta.env['VITE_API_FIXTURES'] === '1';

function readPersistedFixtureFlag(): boolean {
  return safeLocalStorage().getItem(FIXTURE_FLAG_KEY) === '1';
}

let fixtureDetected = FIXTURE_BUILD || readPersistedFixtureFlag();
const fixtureListeners = new Set<(on: boolean) => void>();

export function isFixtureData(): boolean {
  return fixtureDetected;
}

export function subscribeFixtureFlag(listener: (on: boolean) => void): () => void {
  fixtureListeners.add(listener);
  return () => {
    fixtureListeners.delete(listener);
  };
}

const fixtureProbe: Middleware = {
  onResponse({ response }) {
    // fixture 模式下這個旗標由建置決定，任何回應都不能把它關掉。
    if (FIXTURE_BUILD) return undefined;
    const flagged = response.headers.get('X-Stock-Fixture') === '1';
    if (flagged !== fixtureDetected) {
      fixtureDetected = flagged;
      const storage = safeLocalStorage();
      if (flagged) storage.setItem(FIXTURE_FLAG_KEY, '1');
      else storage.removeItem(FIXTURE_FLAG_KEY);
      for (const listener of fixtureListeners) listener(flagged);
    }
    return undefined;
  },
};

/**
 * iOS App 的身分與憑證。網站上兩者都不存在（`isNativeApp()` 為 false、`getToken()` 為
 * null），請求與過去逐位元組相同。
 *
 *  · `X-Client: ios-app` —— 後端看到它，登入／註冊／TOTP 第二步才把 token 放進 body；
 *  · `Authorization: Bearer` —— Keychain 裡那張 token（後端優先於 cookie）。
 */
const nativeAuth: Middleware = {
  async onRequest({ request }) {
    if (isNativeApp()) request.headers.set('X-Client', 'ios-app');
    const token = await getToken();
    if (token !== null) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
};

export const api = createClient<paths>({
  // openapi-fetch 會去掉結尾斜線：'/' → ''，路徑本身以 `/api/...` 開頭。
  baseUrl: API_ORIGIN === '' ? '/' : API_ORIGIN,
  credentials: API_CREDENTIALS,
  headers: { Accept: 'application/json' },
});

api.use(nativeAuth);
api.use(fixtureProbe);

/**
 * 統一的錯誤語意。TanStack Query 只認 throw，而 openapi-fetch 回的是
 * `{ data, error, response }`——把「HTTP 4xx/5xx」翻成例外的地方只能有一個，
 * 否則每個 queryFn 都會長出自己的一套錯誤處理，然後其中一個會忘記 throw。
 *
 * `code` / `messageZh` 來自後端的 `detail: { code, message_zh }`（所有 4xx 的共同形狀）。
 * 表單拿 `code` 決定行為（`captcha_invalid` → 換一張圖），拿 `messageZh` 顯示給人看；
 * `message` 永遠有值（`messageZh ?? code ?? 'HTTP n'`），給不在乎細節的呼叫端。
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly code: string | null;
  readonly messageZh: string | null;

  constructor(
    message: string,
    status: number,
    url: string,
    code: string | null = null,
    messageZh: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.code = code;
    this.messageZh = messageZh;
  }
}

interface FetchResult<T> {
  data?: T | undefined;
  error?: unknown;
  response: Response;
}

/** FastAPI 422 的 `loc` 第一段是參數來源（body / query …），不是欄位名。 */
const LOC_SOURCES: Record<string, true> = {
  body: true,
  query: true,
  path: true,
  header: true,
  cookie: true,
};

/** 從 openapi-fetch 的 `error`（解析過的 JSON body）萃取 code 與可顯示的中文訊息。 */
function describeError(error: unknown): { code: string | null; messageZh: string | null } {
  const detail =
    typeof error === 'object' && error !== null && 'detail' in error ? error.detail : undefined;

  // 422：`[{ loc: ['body', 'captcha_id'], msg, type }, …]` —— 只講第一個欄位。
  if (Array.isArray(detail)) {
    const first: unknown = detail[0];
    const loc: unknown =
      typeof first === 'object' && first !== null && 'loc' in first ? first.loc : undefined;
    const parts: readonly unknown[] = Array.isArray(loc) ? loc : [];
    const field = parts
      .filter(
        (part, index) =>
          !(index === 0 && typeof part === 'string' && Object.hasOwn(LOC_SOURCES, part)),
      )
      .map(String)
      .join('.');
    return {
      code: 'validation_error',
      messageZh: field === '' ? '輸入格式不正確。' : `輸入格式不正確：${field}`,
    };
  }
  // 其餘 4xx：`{ code, message_zh }`（後端所有 HTTPException 的共同形狀）。
  if (typeof detail === 'object' && detail !== null) {
    const code = 'code' in detail ? detail.code : undefined;
    const messageZh = 'message_zh' in detail ? detail.message_zh : undefined;
    return {
      code: typeof code === 'string' ? code : null,
      messageZh: typeof messageZh === 'string' ? messageZh : null,
    };
  }
  if (typeof detail === 'string' && detail !== '') return { code: null, messageZh: detail };
  return { code: null, messageZh: null };
}

export function unwrap<T>(result: FetchResult<T>): T {
  if (result.error !== undefined || result.data === undefined) {
    const { status, url } = result.response;
    const { code, messageZh } = describeError(result.error);
    throw new ApiError(messageZh ?? code ?? `HTTP ${String(status)}`, status, url, code, messageZh);
  }
  return result.data;
}
